// WebView compatibility utilities
export const isWebView = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for common web view patterns
  const webViewPatterns = [
    /wv\)/i, // Android WebView
    /WebView/i, // Generic WebView
    /Version\/.*Mobile.*Safari/i, // iOS WebView
    /FBAN|FBAV/i, // Facebook in-app browser
    /Instagram/i, // Instagram in-app browser
    /Line/i, // Line in-app browser
    /MicroMessenger/i, // WeChat in-app browser
    /QQ/i, // QQ in-app browser
    /UCBrowser/i, // UC Browser
    /MQQBrowser/i, // Mobile QQ Browser
  ];
  
  return webViewPatterns.some(pattern => pattern.test(userAgent));
};

export const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getWebViewInfo = () => {
  const userAgent = navigator.userAgent;
  
  if (/wv\)/i.test(userAgent)) {
    return { type: 'android', version: 'Android WebView' };
  }
  
  if (/Version\/.*Mobile.*Safari/i.test(userAgent)) {
    return { type: 'ios', version: 'iOS WebView' };
  }
  
  if (/FBAN|FBAV/i.test(userAgent)) {
    return { type: 'facebook', version: 'Facebook App' };
  }
  
  if (/Instagram/i.test(userAgent)) {
    return { type: 'instagram', version: 'Instagram App' };
  }
  
  return { type: 'unknown', version: 'Unknown WebView' };
};

export const handleWebViewIssues = () => {
  const webViewInfo = getWebViewInfo();
  
  // Log web view information for debugging
  console.log('WebView detected:', webViewInfo);
  
  // Add web view specific CSS classes
  if (isWebView()) {
    document.body.classList.add('webview');
    document.body.classList.add(`webview-${webViewInfo.type}`);
    
    // Add additional fixes based on webview type
    switch (webViewInfo.type) {
      case 'android':
        // Android WebView specific fixes
        document.body.style.webkitTextSizeAdjust = '100%';
        document.body.style.webkitTransform = 'translateZ(0)';
        break;
        
      case 'ios':
        // iOS WebView specific fixes
        document.body.style.webkitTextSizeAdjust = '100%';
        document.body.style.webkitTouchCallout = 'none';
        document.body.style.webkitOverflowScrolling = 'touch';
        break;
        
      case 'facebook':
        // Facebook in-app browser fixes
        document.body.style.webkitOverflowScrolling = 'touch';
        document.body.style.webkitTransform = 'translate3d(0, 0, 0)';
        break;
        
      case 'instagram':
        // Instagram in-app browser fixes
        document.body.style.webkitOverflowScrolling = 'touch';
        document.body.style.webkitTransform = 'translate3d(0, 0, 0)';
        break;
        
      default:
        // Generic webview fixes
        document.body.style.webkitTextSizeAdjust = '100%';
        break;
    }
  }
  
  return webViewInfo;
};

export const setupWebViewCompatibility = () => {
  // Prevent zoom on input focus (common web view issue)
  const preventZoom = () => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
      );
    }
  };
  
  // Handle input focus events
  document.addEventListener('focusin', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      preventZoom();
    }
  });
  
  // Handle input blur events
  document.addEventListener('focusout', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
      // Restore viewport after a delay
      setTimeout(() => {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 
            'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
          );
        }
      }, 300);
    }
  });
  
  // Fix touch events for webviews
  const fixTouchEvents = () => {
    // Prevent double-tap zoom
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (e) => {
      const now = (new Date()).getTime();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    }, false);
    
    // Fix touch callout
    document.addEventListener('touchstart', (e) => {
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.target.style.webkitTouchCallout = 'none';
      }
    });
  };
  
  // Fix scroll issues in webviews
  const fixScrollIssues = () => {
    // Enable smooth scrolling
    document.documentElement.style.scrollBehavior = 'smooth';
    
    // Fix iOS bounce scroll
    document.body.style.overscrollBehavior = 'none';
    
    // Fix Android scroll issues
    document.body.style.webkitOverflowScrolling = 'touch';
  };
  
  // Fix viewport height issues
  const fixViewportHeight = () => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
  };
  
  // Fix keyboard issues in webviews
  const fixKeyboardIssues = () => {
    // Handle virtual keyboard appearance
    const handleResize = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      
      // Adjust viewport when keyboard appears
      if (window.visualViewport) {
        const viewport = window.visualViewport;
        const height = viewport.height;
        const vhKeyboard = height * 0.01;
        document.documentElement.style.setProperty('--vh-keyboard', `${vhKeyboard}px`);
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }
  };
  
  // Fix form submission issues
  const fixFormIssues = () => {
    // Prevent form submission issues in webviews
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.tagName === 'FORM') {
        // Add a small delay to prevent double submission
        setTimeout(() => {
          form.style.pointerEvents = 'none';
          setTimeout(() => {
            form.style.pointerEvents = 'auto';
          }, 1000);
        }, 100);
      }
    });
  };
  
  // Fix image loading issues
  const fixImageLoading = () => {
    // Lazy load images for better performance
    const images = document.querySelectorAll('img[data-src]');
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.classList.remove('lazy');
          observer.unobserve(img);
        }
      });
    });
    
    images.forEach(img => imageObserver.observe(img));
  };
  
  // Fix performance issues
  const fixPerformanceIssues = () => {
    // Debounce scroll events
    let scrollTimeout;
    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        // Handle scroll events here
      }, 16); // ~60fps
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Optimize animations
    const style = document.createElement('style');
    style.textContent = `
      .webview * {
        will-change: auto;
      }
      .webview .animate {
        will-change: transform, opacity;
      }
    `;
    document.head.appendChild(style);
  };
  
  // Initialize all fixes
  fixTouchEvents();
  fixScrollIssues();
  fixViewportHeight();
  fixKeyboardIssues();
  fixFormIssues();
  fixImageLoading();
  fixPerformanceIssues();
  
  // Initialize web view detection
  handleWebViewIssues();
};

// Debug utility for webview issues
export const debugWebView = () => {
  const webViewInfo = getWebViewInfo();
  const isWebViewDetected = isWebView();
  const isMobileDetected = isMobile();
  
  console.group('🔍 WebView Debug Information');
  console.log('WebView Detected:', isWebViewDetected);
  console.log('Mobile Detected:', isMobileDetected);
  console.log('WebView Info:', webViewInfo);
  console.log('User Agent:', navigator.userAgent);
  console.log('Viewport Width:', window.innerWidth);
  console.log('Viewport Height:', window.innerHeight);
  console.log('Device Pixel Ratio:', window.devicePixelRatio);
  console.log('Touch Support:', 'ontouchstart' in window);
  console.log('CSS Classes on body:', document.body.className);
  console.log('Visual Viewport Available:', !!window.visualViewport);
  
  if (window.visualViewport) {
    console.log('Visual Viewport Height:', window.visualViewport.height);
    console.log('Visual Viewport Width:', window.visualViewport.width);
  }
  
  console.groupEnd();
  
  return {
    isWebView: isWebViewDetected,
    isMobile: isMobileDetected,
    webViewInfo,
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    devicePixelRatio: window.devicePixelRatio,
    touchSupport: 'ontouchstart' in window,
    bodyClasses: document.body.className,
    visualViewport: window.visualViewport ? {
      height: window.visualViewport.height,
      width: window.visualViewport.width
    } : null
  };
};
